import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { CommonState } from '../../../redux';
import { userStore } from '../../../redux/entities/user';
import { Formik, Form } from 'formik';
import { Grid, TextInput, Group, Text, Avatar, Loader } from '@mantine/core';
import { getErrorResponseMessage, isBadRequest } from '../../../util/http';
import { resetAuthenticatedPassword, type PasswordResetRequest } from '../../../api/authApi';
import { noProfilePicture } from '../../../appTheme';
import { getBase64 } from '../../../util/file';
import type { UpdateProfilePictureRequest } from './components/ProfilePicture';

type OwnProps = {
  onClose?: () => void;
  formId?: string; // default: 'profile-form'
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

// Form type for authenticated password reset (email + password fields)
type AuthenticatedPasswordResetForm = Omit<PasswordResetRequest, 'passwordResetToken'>;

const ProfileModal: React.FC<Props> = ({ currentUser, onClose, formId = 'profile-form', actions }) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initialValues: AuthenticatedPasswordResetForm = { password: '', confirmPassword: '', email: currentUser?.email ?? '' } as AuthenticatedPasswordResetForm;

  // Trigger native file picker when avatar is clicked
  const onAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const onFileSelected: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.currentTarget.files?.[0];
    if (!file || !currentUser?.id) return;
    setErrorMessage(undefined);
    setUploadingAvatar(true);
    try {
      const imageBase64 = await getBase64(file as any);
      const req: UpdateProfilePictureRequest = {
        userId: currentUser.id,
        imageBase64: imageBase64 as any,
        fileName: file.name,
      };
      await actions.updateProfilePicture(req);
      // On success: clear input; avatar will update via currentUser from store
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      setErrorMessage(isBadRequest(e) ? getErrorResponseMessage(e) : 'Failed to upload profile picture');
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <Formik<AuthenticatedPasswordResetForm>
      initialValues={initialValues}
      onSubmit={async (vals, { setSubmitting: setFormikSubmitting }) => {
        setErrorMessage(undefined);
        setSubmitting(true);
        try {
          if ((vals.password || '') !== (vals.confirmPassword || '')) {
            setErrorMessage('Passwords do not match');
            return;
          }
          await resetAuthenticatedPassword({
            password: vals.password,
            confirmPassword: vals.confirmPassword,
            email: currentUser?.email ?? ''
          });
          onClose?.();
        } catch (e) {
          if (isBadRequest(e)) {
            setErrorMessage(getErrorResponseMessage(e));
          } else {
            setErrorMessage('Failed to update password');
          }
        } finally {
          setSubmitting(false);
          setFormikSubmitting(false);
        }
      }}
      enableReinitialize
    >
      {({ values, setFieldValue, handleSubmit }) => (
        <Form id={formId} onSubmit={handleSubmit}>
          <Group align="center" spacing="md" style={{ marginBottom: 12 }}>
            <div style={{ position: 'relative' }}>
              <Avatar
                src={currentUser?.profilePicturePath || noProfilePicture()}
                alt="Profile picture"
                radius="xl"
                size={72}
                style={{ cursor: 'pointer', border: '2px solid #e9ecef' }}
                // Ensure oversized images shrink to fit inside the circular avatar without cropping
                styles={{ image: { objectFit: 'contain' } }}
                onClick={onAvatarClick}
              />
              {uploadingAvatar && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', borderRadius: 9999 }}>
                  <Loader size="sm" />
                </div>
              )}
              {/* Hidden file input to choose new avatar */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={onFileSelected}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text fw={700} size="lg">{currentUser?.name}</Text>
              <Text size="sm" color="dimmed">{currentUser?.email}</Text>
            </div>
          </Group>

          <Grid gutter="md">
            {/* Password fields only */}
            <Grid.Col span={12} md={6}>
              <TextInput
                label="Password"
                type="password"
                value={values.password}
                onChange={(e) => setFieldValue('password', e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={12} md={6}>
              <TextInput
                label="Confirm Password"
                type="password"
                value={values.confirmPassword}
                onChange={(e) => setFieldValue('confirmPassword', e.currentTarget.value)}
              />
            </Grid.Col>

            {errorMessage && (
              <Grid.Col span={12}>
                <div style={{ color: '#c92a2a' }}>{errorMessage}</div>
              </Grid.Col>
            )}
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: CommonState) => ({
  currentUser: userStore.selectors.getCurrentUser(state)
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: bindActionCreators({
    updateProfilePicture: userStore.actions.updateProfilePicture,
  }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(ProfileModal);
